import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, XCircle } from 'lucide-react';
import { prescriptionCancellationService } from '@/services/prescriptionCancellationService';
import { useToast } from '@/hooks/use-toast';
import { Prescription } from '@/lib/types';

interface CancelPrescriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prescription: Prescription | null;
  onSuccess?: () => void;
}

const CancelPrescriptionDialog: React.FC<CancelPrescriptionDialogProps> = ({
  open,
  onOpenChange,
  prescription,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!prescription) return;

    if (!reason.trim()) {
      toast({
        variant: 'destructive',
        title: 'Reason required',
        description: 'Please provide a reason for cancellation',
      });
      return;
    }

    try {
      setIsLoading(true);
      await prescriptionCancellationService.requestCancellation(prescription.id, {
        reason: reason.trim(),
      });

      toast({
        variant: 'success',
        title: 'Cancellation request submitted',
        description: 'Your cancellation request has been submitted and is pending admin approval',
      });

      setReason('');
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error('Error submitting cancellation request:', error);
      toast({
        variant: 'destructive',
        title: 'Error submitting cancellation request',
        description: error.response?.data?.error?.message || error.message || 'Please try again later',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setReason('');
      onOpenChange(false);
    }
  };

  if (!prescription) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-red-50">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold">
                Cancel Order
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-500 mt-1">
                Request cancellation for this order. An admin will review your request.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm font-medium">
              Cancellation Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="Please provide a reason for cancelling this order..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isLoading}
              rows={4}
              className="resize-none"
              maxLength={1000}
            />
            <p className="text-xs text-slate-500">
              {reason.length}/1000 characters
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Your cancellation request will be reviewed by an administrator. 
              If approved, a full refund will be processed and the order status will be updated to cancelled.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || !reason.trim()}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelPrescriptionDialog;

